// src/ryan/components/product-blocks/DemoBlockShared.tsx
// Estrutura editorial compartilhada pelos blocos de demonstração (Apostila/Flashcards):
// cabeçalho (label + headline + subheadline), lista de pilares e painel de fechamento.
// Mantém os dois blocos visualmente consistentes sem duplicar marcação/CSS.

import React from 'react';
import { BookOpen } from 'lucide-react';
import { DESC_ICONS } from '../../../components/admin/products/tabs/CreateProducts';
import './DemoBlockShared.css';

export interface DemoPillar {
    id: string;
    icon: string;
    title: string;
    text: string;
}

export interface DemoClosing {
    lead: string;
    emphasis: string;
    items: string[];
}

export function resolvePillarIcon(iconName: string): React.ComponentType<{ size?: number; strokeWidth?: number }> {
    return DESC_ICONS.find(ic => ic.name === iconName)?.component ?? BookOpen;
}

export const DemoHeader: React.FC<{ label: string; headline: string; subheadline: string; visible: boolean }> = ({ label, headline, subheadline, visible }) => {
    if (!label && !headline && !subheadline) return null;
    return (
        <div className={`rpb-demo-header ${visible ? 'rpb-demo-header--visible' : ''}`}>
            {label && <p className="rpb-demo-label">{label}</p>}
            {headline && <h2 className="rpb-demo-headline">{headline}</h2>}
            {subheadline && <p className="rpb-demo-subheadline">{subheadline}</p>}
        </div>
    );
};

// layout='column' (default) é o uso original — coluna vertical estreita ao
// lado do mock, usada por ApostilaDemoBlock/FlashcardsDemoBlock, intocada.
// layout='row' é aditivo, só pro MetodoDemoBlock: grade horizontal de 3
// colunas (quebra pra empilhado no mobile via CSS), reaproveitando os
// MESMOS dados/ícones/classes internas do pilar — só o container externo
// muda.
export const DemoPillarsList: React.FC<{ pillars: DemoPillar[]; visible: boolean; layout?: 'column' | 'row' }> = ({ pillars, visible, layout = 'column' }) => {
    if (pillars.length === 0) return null;
    return (
        <div className={`rpb-demo-pillars ${layout === 'row' ? 'rpb-demo-pillars--row' : ''} ${visible ? 'rpb-demo-pillars--visible' : ''}`}>
            {pillars.map((pillar, i) => {
                const Icon = resolvePillarIcon(pillar.icon);
                return (
                    <div
                        key={pillar.id}
                        className="rpb-demo-pillar"
                        style={{ '--rpb-pillar-i': i } as React.CSSProperties}
                    >
                        <div className="rpb-demo-pillar-icon">
                            <Icon size={16} strokeWidth={1.75} />
                        </div>
                        <div className="rpb-demo-pillar-body">
                            <h3 className="rpb-demo-pillar-title">{pillar.title}</h3>
                            <p className="rpb-demo-pillar-text">{pillar.text}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const DemoClosingPanel: React.FC<{ closing: DemoClosing; visible: boolean }> = ({ closing, visible }) => {
    if (!closing.lead && !closing.emphasis && closing.items.length === 0) return null;
    return (
        <div className={`rpb-demo-closing ${visible ? 'rpb-demo-closing--visible' : ''}`}>
            {(closing.lead || closing.emphasis) && (
                <p className="rpb-demo-closing-phrase">
                    {closing.lead}
                    {closing.lead && closing.emphasis ? ' ' : ''}
                    {closing.emphasis && <strong>{closing.emphasis}</strong>}
                </p>
            )}
            {closing.items.length > 0 && (
                <div className="rpb-demo-closing-list">
                    {closing.items.map((item, i) => (
                        <div key={i} className="rpb-demo-closing-item">
                            <span className="rpb-demo-closing-dot" />
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
